/**
 * Dated tax constants. Amounts are integers in öre; rates are kept as
 * integers where money is derived from them so no float ever touches an
 * amount.
 */
import type { Ore } from '../money';

/**
 * Half a prisbasbelopp per calendar year (the threshold above which a
 * purchase is an asset to depreciate instead of a förbrukningsinventarie).
 * 2025: 58 800 kr / 2 = 29 400 kr. 2026: 59 900 kr / 2 = 29 950 kr.
 */
export const HALF_PRISBASBELOPP: Readonly<Record<number, Ore>> = {
  2025: 2_940_000,
  2026: 2_995_000,
};

/**
 * Threshold for a rules year. Unknown years use the latest known year at or
 * before the requested one; years before the first known year use the first
 * known value. Never throws so a broken fiscal year always gets a number.
 */
export function halfPrisbasbeloppFor(year: number): Ore {
  const known = Object.keys(HALF_PRISBASBELOPP)
    .map(Number)
    .sort((a, b) => a - b);
  let chosen = known[0]!;
  for (const y of known) {
    if (y <= year) chosen = y;
  }
  return HALF_PRISBASBELOPP[chosen]!;
}

/** Bolagsskatt (aktiebolag, ekonomiska föreningar, BRF on taxable income). */
export const CORPORATE_TAX_RATE = 0.206;
/** Same rate as an integer per mille, for öre arithmetic. */
export const CORPORATE_TAX_PER_MILLE = 206;

/**
 * Proposed corporate tax on a taxable result, in whole kronor (öre value
 * divisible by 100). Zero for a loss. Integer arithmetic only.
 */
export function corporateTaxOre(taxableResultOre: Ore): Ore {
  if (!Number.isInteger(taxableResultOre) || taxableResultOre <= 0) return 0;
  const taxOre = (taxableResultOre * CORPORATE_TAX_PER_MILLE) / 1000;
  return Math.round(taxOre / 100) * 100;
}

/** Representation: VAT is deductible on at most 300 kr per person (excl. VAT). */
export const REPRESENTATION_VAT_BASE_PER_PERSON_ORE: Ore = 30_000;
/** Enklare förtäring: at most 60 kr per person (excl. VAT) is a deductible cost. */
export const ENKLARE_FORTARING_MAX_PER_PERSON_ORE: Ore = 6_000;

/** Highest deductible VAT for a representation meal: persons × 300 kr × rate. */
export function representationVatCapOre(persons: number, ratePercent: number): Ore {
  const p = Math.max(1, Math.floor(persons));
  // 300 kr × rate % = 300 × rate öre exactly (30 000 öre × rate / 100).
  return p * 300 * ratePercent;
}
